import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { 
  LayoutDashboard, 
  Users, 
  Download, 
  Monitor, 
  LogOut,
  Server,
  Menu,
  X,
  Settings,
  CreditCard
} from "lucide-react";
import { Button } from "./ui/button";
import { useState } from "react";

const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const navItems = [
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/dashboard/clients", icon: Users, label: "Clients" },
    { to: "/dashboard/agents", icon: Download, label: "Agents" },
    { to: "/dashboard/subscription", icon: CreditCard, label: "Abonnement" },
    { to: "/dashboard/settings", icon: Settings, label: "Parametres" },
  ];

  const NavItem = ({ to, icon: Icon, label }) => (
    <NavLink
      to={to}
      end={to === "/dashboard"}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-3 rounded-md transition-all duration-200 ${
          isActive
            ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/20"
            : "text-slate-400 hover:text-white hover:bg-slate-800"
        }`
      }
      onClick={() => setSidebarOpen(false)}
    >
      <Icon size={20} />
      <span className="font-medium">{label}</span>
    </NavLink>
  );

  return (
    <div className="min-h-screen bg-[#07090E] flex">
      {/* Mobile menu button */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-md bg-slate-800 text-white"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        data-testid="mobile-menu-btn"
      >
        {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-[#0D111A] border-r border-slate-800/60 transform transition-transform duration-300 ease-in-out ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-800/60">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center">
                <Server className="text-cyan-400" size={24} />
              </div>
              <div>
                <h1 className="font-bold text-lg text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>ServeurOnline</h1>
                <p className="text-xs text-slate-500">Backup Monitor</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            {navItems.map((item) => (
              <NavItem key={item.to} {...item} />
            ))}
            
            {/* External link to monitoring */}
            <a
              href="/monitoring"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-4 py-3 rounded-md text-slate-400 hover:text-white hover:bg-slate-800 transition-all duration-200"
              data-testid="monitoring-link"
            >
              <Monitor size={20} />
              <span className="font-medium">Mode Monitoring</span>
            </a>
          </nav>

          {/* User info & logout */}
          <div className="p-4 border-t border-slate-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-white">{user?.email}</p>
                <p className="text-xs text-slate-500">{user?.role}</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                className="text-slate-400 hover:text-white hover:bg-slate-800"
                data-testid="logout-btn"
              >
                <LogOut size={20} />
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <div className="p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default Layout;
